import React, { useEffect, useState } from 'react';
import EnterpriseForm from './EnterpriseForm';
import ConceptForm from './ConceptForm';
import FranchiseeForm from './FranchiseeForm';
import StoreForm from './StoreForm';
import StoreEditForm from './StoreEditForm';
import OrganizationalUnitForm from './OrganizationalUnitForm';
import DaypartConfiguration from './enterprise/DaypartConfiguration';
import ReasonCodesManagement from './enterprise/ReasonCodesManagement';
import TableChartManager from './restaurant/TableChartManager';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { getUserRole, hasPermission } from '../utils/roleUtils';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiGatewayMigration';


interface Enterprise {
  enterpriseId: string;
  name: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface Concept {
  id: string;
  conceptId?: string; // Backend field
  name: string;
  description: string;
  enterpriseId: string;
  enterpriseName: string;
  status: string;
  storeCount: number;
  createdAt: string;
}

interface Franchisee {
  id?: string; // For backward compatibility
  franchiseeId?: string; // Backend field
  name: string;
  legalName: string;
  enterpriseId: string;
  enterpriseName: string;
  status: string;
  storeCount: number;
  orgUnitCount: number;
  createdAt: string;
}

interface OrganizationalUnit {
  id: string;
  name: string;
  type: string;
  franchiseeId: string;
  franchiseeName: string;
  parentId?: string;
  storeCount: number;
  status: string;
}

interface Store {
  id: string;
  storeId?: string; // Backend field
  name: string;
  storeCode?: string; // Store code for Android POS devices
  address: string | {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  phone: string;
  conceptId: string;
  conceptName: string;
  franchiseeId?: string;
  franchiseeName?: string;
  orgUnitId?: string;
  orgUnitName?: string;
  status: string;
  type: string;
  storeType?: string;
}

const RestaurantManagement: React.FC = () => {
  const { getIdToken, user, session } = useAuth();
  const [activeTab, setActiveTab] = useState('enterprises');

  // Helper function to format address
  const formatAddress = (address: string | { street?: string; city?: string; state?: string; zipCode?: string; country?: string; }): string => {
    if (typeof address === 'string') {
      return address;
    }
    
    if (typeof address === 'object' && address !== null) {
      const parts = [];
      if (address.street) parts.push(address.street);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.zipCode) parts.push(address.zipCode);
      return parts.join(', ') || 'No address provided';
    }
    
    return 'No address provided';
  };
  const [loading, setLoading] = useState(true);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [franchisees, setFranchisees] = useState<Franchisee[]>([]);
  const [orgUnits, setOrgUnits] = useState<OrganizationalUnit[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState('');
  const [selectedEnterpriseForDayparts, setSelectedEnterpriseForDayparts] = useState<string | null>(null);
  const [selectedEnterpriseForReasonCodes, setSelectedEnterpriseForReasonCodes] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedStoreForTableChart, setSelectedStoreForTableChart] = useState<string | null>(null);
  const [showOperationsDropdown, setShowOperationsDropdown] = useState<{ [key: string]: boolean }>({});

  // Use centralized role management system
  const userRole = getUserRole(user, session);
  const isSystemAdmin = hasPermission(user, 'canAccessAdmin', session);

  // Handler functions for buttons
  const handleEdit = (item: any, type: string) => {
    console.log(`Opening edit form for ${type}:`, item);
    console.log(`Available IDs: id=${item.id}, storeId=${item.storeId}, enterpriseId=${item.enterpriseId}`);
    setEditingItem({ ...item, type });
    setShowEditModal(true);
  };

  const handleViewDetails = (item: any, type: string) => {
    setSelectedItem({ ...item, type });
    setShowDetailsModal(true);
  };

  const toggleOperationsDropdown = (storeId: string) => {
    setShowOperationsDropdown(prev => ({
      ...prev,
      [storeId]: !prev[storeId]
    }));
  };

  const handleTableChartManagement = (store: Store) => {
    console.log('🎯 Table Chart Management clicked for store:', store);
    console.log('🔧 Setting selectedStoreForTableChart to:', store.storeId || store.id);
    setSelectedStoreForTableChart(store.storeId || store.id);
    setShowOperationsDropdown({});
  };

  const handleSaveEdit = async (formData: any) => {
    try {
      const { type, storeId, ...data } = formData;
      let itemId = '';
      let response;
      
      console.log(`🔧 Updating ${type} with data:`, data);

      switch (type) {
        case 'enterprise':
          itemId = editingItem.enterpriseId || editingItem.id;
          response = await apiPut('enterprise', data, itemId);
          break;
        case 'concept':
          itemId = editingItem.conceptId || editingItem.id;
          const enterpriseId = editingItem.enterpriseId;
          response = await apiPut('concepts', data, enterpriseId, itemId);
          break;
        case 'franchisee':
          itemId = editingItem.franchiseeId || editingItem.id;
          const franchiseeEnterpriseId = editingItem.enterpriseId;
          response = await apiPut('franchisees', data, franchiseeEnterpriseId, itemId);
          break;
        case 'store':
          itemId = storeId;
          response = await apiPut('store', data, itemId);
          break;
      }

      const result = await response.json();
      console.log('✅ Update successful:', result);
      
      // Show success message with any special messages from backend
      if (result.message) {
        alert(result.message);
      } else {
        alert(`${type.charAt(0).toUpperCase() + type.slice(1)} updated successfully!`);
      }
      
      setShowEditModal(false);
      setEditingItem(null);
      
      // Reload data to reflect changes
      window.location.reload();
    } catch (error: any) {
      console.error('❌ Error updating item:', error);
      alert(`Error updating ${formData.type}: ${error.message}`);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load enterprises using API Gateway migration utility
        const enterpriseData = await apiGet('enterprises');
        console.log('✅ Enterprise data received:', enterpriseData);
        console.log('📊 Number of enterprises:', enterpriseData.data?.length || enterpriseData?.length || 0);
        
        // Handle both wrapped and direct responses
        const enterpriseList = enterpriseData.data || enterpriseData || [];
        setEnterprises(enterpriseList);
        console.log('🏢 Enterprises state updated, count:', enterpriseList.length);
          
        // Load concepts for each enterprise
        const allConcepts: Concept[] = [];
        const allFranchisees: Franchisee[] = [];
        
        for (const enterprise of enterpriseList) {
          try {
            // Load concepts using API Gateway migration utility
            const conceptData = await apiGet('concepts', enterprise.enterpriseId);
            const enterpriseConcepts = (conceptData.data || []).map((concept: any) => ({
              ...concept,
              enterpriseName: enterprise.name,
              storeCount: 0 // TODO: Load actual store count
            }));
            allConcepts.push(...enterpriseConcepts);
          } catch (error) {
            console.error('Error loading concepts for enterprise:', enterprise.enterpriseId, error);
          }
          
          try {
            // Load franchisees using API Gateway migration utility
            const franchiseeData = await apiGet('franchisees', enterprise.enterpriseId);
            const enterpriseFranchisees = (franchiseeData.data || []).map((franchisee: any) => ({
              ...franchisee,
              enterpriseName: enterprise.name,
              storeCount: 0, // TODO: Load actual store count
              orgUnitCount: 0 // TODO: Load actual org unit count
            }));
            allFranchisees.push(...enterpriseFranchisees);
          } catch (error) {
            console.error('Error loading franchisees for enterprise:', enterprise.enterpriseId, error);
          }
        }
        
        setConcepts(allConcepts);
        setFranchisees(allFranchisees);
        
        // Load stores using API Gateway migration utility
        const storeData = await apiGet('stores');
        console.log('✅ Store data received:', storeData);
        // Handle both direct array and wrapped response
        const storeList = Array.isArray(storeData) ? storeData : (storeData.data || []);
        console.log('📊 Number of stores:', storeList.length);
        setStores(storeList);
        
        // For now, keep organizational units empty until we implement that endpoint
        setOrgUnits([]);
        
      } catch (error) {
        console.error('❌ Error loading data:', error);
        setEnterprises([]);
        setConcepts([]);
        setFranchisees([]);
        setOrgUnits([]);
        setStores([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleAddNew = (type: string) => {
    setAddType(type);
    setShowAddModal(true);
  };

  const handleCreateEnterprise = async (formData: any) => {
    try {
      
      const response = await apiPost('enterprises', {
        name: formData.name,
        description: formData.description,
        contactInfo: {
          email: formData.email,
          phone: formData.phone,
        },
      });

      const result = await response.json();
      console.log('📦 Raw response:', result);
      
      // Handle different response formats
      const enterpriseData = Array.isArray(result) ? result[0] : (result.data || result);
      console.log('✅ Enterprise created successfully:', enterpriseData);
      
      // Update the enterprises list immediately
      setEnterprises(prev => [...prev, enterpriseData]);
      setShowAddModal(false);
      
      // Show success message
      alert(`Enterprise "${enterpriseData.name}" created successfully!`);
    } catch (error: any) {
      console.error('❌ Error creating enterprise:', error);
      alert(`Error creating enterprise: ${error.message}`);
    }
  };

  const handleCreateConcept = async (formData: any) => {
    try {
      
      const response = await apiPost('concepts', {
        name: formData.name,
        description: formData.description,
      }, formData.enterpriseId);

      const result = await response.json();
      const enterprise = enterprises.find(e => e.enterpriseId === formData.enterpriseId);
      const newConcept = {
        ...result.data,
        enterpriseName: enterprise?.name || '',
        storeCount: 0,
      };
      setConcepts(prev => [...prev, newConcept]);
      setShowAddModal(false);
      console.log('✅ Concept created successfully:', result.data);
    } catch (error: any) {
      console.error('❌ Error creating concept:', error);
      alert(`Error creating concept: ${error.message}`);
    }
  };

  const handleCreateFranchisee = async (formData: any) => {
    try {
      
      const response = await apiPost('franchisees', {
        name: formData.name,
        legalName: formData.legalName,
        contactInfo: {
          email: formData.email,
          phone: formData.phone,
        },
      }, formData.enterpriseId);

      const result = await response.json();
      const enterprise = enterprises.find(e => e.enterpriseId === formData.enterpriseId);
      const newFranchisee = {
        ...result.data,
        enterpriseName: enterprise?.name || '',
        storeCount: 0,
        orgUnitCount: 0,
      };
      setFranchisees(prev => [...prev, newFranchisee]);
      setShowAddModal(false);
      console.log('✅ Franchisee created successfully:', result.data);
    } catch (error: any) {
      console.error('❌ Error creating franchisee:', error);
      alert(`Error creating franchisee: ${error.message}`);
    }
  };

  // Function to generate store code automatically
  const generateStoreCode = (storeName: string, existingStores: any[]): string => {
    // Extract city/location from address or use store name
    const baseCode = storeName
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '') // Remove special chars
      .substring(0, 3) // Take first 3 chars
      .padEnd(3, 'X'); // Pad if needed
    
    // Find next available number
    let counter = 1;
    let storeCode = `${baseCode}${counter.toString().padStart(3, '0')}`;
    
    while (existingStores.some(store => store.storeCode === storeCode)) {
      counter++;
      storeCode = `${baseCode}${counter.toString().padStart(3, '0')}`;
    }
    
    return storeCode;
  };

  const handleCreateStore = async (formData: any) => {
    try {
      // Get enterprise ID from the selected concept
      const selectedConcept = concepts.find(c => 
        c.id === formData.conceptId || 
        c.conceptId === formData.conceptId
      );
      const enterpriseId = selectedConcept?.enterpriseId;
      
      // Generate store code automatically
      const storeCode = generateStoreCode(formData.name, stores);
      
      console.log('🔍 Looking for concept:', formData.conceptId);
      console.log('📋 Available concepts:', concepts.map(c => ({ id: c.id, conceptId: c.conceptId, name: c.name })));
      console.log('✅ Selected concept:', selectedConcept);
      
      if (!enterpriseId) {
        console.error('Cannot create store: No enterprise ID found for selected concept');
        alert('Error: Cannot determine enterprise for selected concept');
        return;
      }

      const response = await fetch(API_ENDPOINTS.stores(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getIdToken()}`, // Add auth header
        },
        body: JSON.stringify({
          name: formData.name,
          storeCode: storeCode, // AUTO-GENERATED: Include store code
          conceptId: formData.conceptId,
          franchiseeId: formData.franchiseeId || null,
          enterpriseId: enterpriseId, // CRITICAL: Include enterprise ID for hierarchy
          address: formData.address,
          phone: formData.phone,
          email: formData.email,
          storeType: formData.storeType,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const newStore = {
          ...result.data,
          conceptName: concepts.find(c => c.id === formData.conceptId)?.name || '',
          franchiseeName: formData.franchiseeId 
            ? franchisees.find(f => f.id === formData.franchiseeId)?.name || null
            : null,
          type: result.data.storeType,
        };
        setStores(prev => [...prev, newStore]);
        setShowAddModal(false);
        console.log('Store created successfully:', result.data);
        alert(`✅ Store "${formData.name}" created successfully!\n🏪 Store Code: ${storeCode}\n\nThis code can be used to register Android POS devices.`);
      } else {
        const error = await response.json();
        console.error('Failed to create store:', error.message);
      }
    } catch (error) {
      console.error('Error creating store:', error);
    }
  };

  const handleCreateOrgUnit = async (formData: any) => {
    try {
      const response = await fetch(API_ENDPOINTS.organizationalUnits(formData.enterpriseId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getIdToken()}`, // Add auth header
        },
        body: JSON.stringify({
          name: formData.name,
          type: formData.type,
          franchiseeId: formData.franchiseeId,
          parentId: formData.parentId || null,
          description: formData.description,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const enterprise = enterprises.find(e => e.enterpriseId === formData.enterpriseId);
        const franchisee = franchisees.find(f => (f.franchiseeId || f.id) === formData.franchiseeId);
        const newOrgUnit = {
          ...result.data,
          enterpriseName: enterprise?.name || '',
          franchiseeName: franchisee?.name || '',
          storeCount: 0,
        };
        setOrgUnits(prev => [...prev, newOrgUnit]);
        setShowAddModal(false);
        console.log('Organizational unit created successfully:', result.data);
      } else {
        const error = await response.json();
        console.error('Failed to create organizational unit:', error.message);
      }
    } catch (error) {
      console.error('Error creating organizational unit:', error);
    }
  };

  if (loading) {
    return <div>Loading location hierarchy...</div>;
  }

  if (!isSystemAdmin) {
    return (
      <div>
        <div className="dashboard-card">
          <h3>Access Restricted</h3>
          <p>Location hierarchy management is available for system administrators only.</p>
          <p>Regular users can view and manage individual stores through the dashboard.</p>
          <p style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '14px' }}>
            Contact your administrator if you need access to this feature.
          </p>
        </div>
      </div>
    );
  }

  return <div className="restaurant-management-container">
      <div className="dashboard-card" style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ margin: 0 }}>Manage the complete enterprise structure from top to bottom</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <select 
              className="form-control" 
              style={{ width: 'auto' }}
              onChange={(e) => e.target.value && handleAddNew(e.target.value)}
              value=""
            >
              <option value="">Add New...</option>
              <option value="enterprise">Enterprise</option>
              <option value="concept">Concept</option>
              <option value="franchisee">Franchisee</option>
              <option value="org_unit">Organizational Unit</option>
              <option value="store">Store</option>
            </select>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid #e9ecef', marginBottom: '32px' }}>
        {[
          { key: 'enterprises', label: 'Enterprises', count: enterprises.length },
          { key: 'concepts', label: 'Concepts', count: concepts.length },
          { key: 'franchisees', label: 'Franchisees', count: franchisees.length },
          { key: 'org_units', label: 'Org Units', count: orgUnits.length },
          { key: 'stores', label: 'Stores', count: stores.length }
        ].map(tab => (
          <button
            key={tab.key}
            className={`btn ${activeTab === tab.key ? 'btn-primary' : 'btn-outline'}`}
            style={{ 
              borderRadius: '0', 
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #007bff' : '2px solid transparent',
              background: activeTab === tab.key ? '#f8f9fa' : 'transparent',
              color: activeTab === tab.key ? '#007bff' : '#6c757d'
            }}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Enterprises Tab */}
      {activeTab === 'enterprises' && (
        <div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Enterprise</th>
                  <th>Slug</th>
                  <th>Concepts</th>
                  <th>Franchisees</th>
                  <th>Total Stores</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enterprises.map((enterprise) => (
                  <tr key={enterprise.enterpriseId}>
                    <td>
                      <div>
                        <div style={{ fontWeight: '600', color: '#212529' }}>{enterprise.name}</div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>ID: {enterprise.enterpriseId}</div>
                      </div>
                    </td>
                    <td style={{ color: '#495057' }}>{enterprise.status}</td>
                    <td style={{ color: '#495057' }}>-</td>
                    <td style={{ color: '#495057' }}>-</td>
                    <td style={{ color: '#495057' }}>-</td>
                    <td>
                      <span className={`status-badge ${enterprise.status}`}>
                        {enterprise.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          className="btn-modern-secondary" 
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                          onClick={() => handleEdit(enterprise, 'enterprise')}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn-modern-secondary" 
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                          onClick={() => handleViewDetails(enterprise, 'enterprise')}
                        >
                          View Details
                        </button>
                        <button 
                          className="btn-modern-accent" 
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                          onClick={() => setSelectedEnterpriseForDayparts(enterprise.enterpriseId)}
                        >
                          Configure Dayparts
                        </button>
                        <button 
                          className="btn-modern-primary" 
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                          onClick={() => setSelectedEnterpriseForReasonCodes(enterprise.enterpriseId)}
                        >
                          Reason Codes
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Daypart Configuration Section */}
          {selectedEnterpriseForDayparts && (
            <div style={{ marginTop: '48px', borderTop: '1px solid #e9ecef', paddingTop: '32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3>Daypart Configuration</h3>
                <button
                  className="btn btn-outline"
                  onClick={() => setSelectedEnterpriseForDayparts(null)}
                >
                  Close Configuration
                </button>
              </div>
              <p style={{ color: '#6c757d', marginBottom: '24px' }}>
                Configure operating periods for enterprise: <strong>
                  {enterprises.find(e => e.enterpriseId === selectedEnterpriseForDayparts)?.name}
                </strong>
              </p>
              <DaypartConfiguration 
                enterpriseId={selectedEnterpriseForDayparts}
                onSave={(config) => {
                  console.log('Daypart configuration saved:', config);
                  // Optionally close the configuration panel after saving
                  // setSelectedEnterpriseForDayparts(null);
                }}
              />
            </div>
          )}

          {/* Reason Codes Management Section */}
          {selectedEnterpriseForReasonCodes && (
            <div style={{ marginTop: '48px', borderTop: '1px solid #e9ecef', paddingTop: '32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3>Reason Codes Management</h3>
                <button
                  className="btn btn-outline"
                  onClick={() => setSelectedEnterpriseForReasonCodes(null)}
                >
                  Close Management
                </button>
              </div>
              <p style={{ color: '#6c757d', marginBottom: '24px' }}>
                Configure reason codes for POS operations: <strong>
                  {enterprises.find(e => e.enterpriseId === selectedEnterpriseForReasonCodes)?.name}
                </strong>
              </p>
              <ReasonCodesManagement 
                enterpriseId={selectedEnterpriseForReasonCodes}
                onClose={() => setSelectedEnterpriseForReasonCodes(null)}
              />
            </div>
          )}
        </div>
      )}

      {/* Concepts Tab */}
      {activeTab === 'concepts' && (
        <div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Concept</th>
                  <th>Enterprise</th>
                  <th>Description</th>
                  <th>Stores</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {concepts.map((concept) => (
                  <tr key={concept.id}>
                    <td>
                      <div>
                        <div style={{ fontWeight: '600', color: '#212529' }}>{concept.name}</div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>ID: {concept.id}</div>
                      </div>
                    </td>
                    <td style={{ color: '#495057' }}>{concept.enterpriseName}</td>
                    <td style={{ color: '#495057' }}>{concept.description}</td>
                    <td style={{ color: '#495057' }}>{concept.storeCount}</td>
                    <td>
                      <span className={`status-badge ${concept.status}`}>
                        {concept.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn-modern-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>
                          Edit
                        </button>
                        <button className="btn-modern-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>
                          Menu Config
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Franchisees Tab */}
      {activeTab === 'franchisees' && (
        <div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Franchisee</th>
                  <th>Legal Name</th>
                  <th>Enterprise</th>
                  <th>Org Units</th>
                  <th>Stores</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {franchisees.map((franchisee) => (
                  <tr key={franchisee.franchiseeId || franchisee.id}>
                    <td>
                      <div>
                        <div style={{ fontWeight: '600', color: '#212529' }}>{franchisee.name}</div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>ID: {franchisee.franchiseeId || franchisee.id}</div>
                      </div>
                    </td>
                    <td style={{ color: '#495057' }}>{franchisee.legalName}</td>
                    <td style={{ color: '#495057' }}>{franchisee.enterpriseName}</td>
                    <td style={{ color: '#495057' }}>{franchisee.orgUnitCount}</td>
                    <td style={{ color: '#495057' }}>{franchisee.storeCount}</td>
                    <td>
                      <span className={`status-badge ${franchisee.status}`}>
                        {franchisee.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn-modern-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>
                          Edit
                        </button>
                        <button className="btn-modern-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>
                          Manage
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Organizational Units Tab */}
      {activeTab === 'org_units' && (
        <div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Organizational Unit</th>
                  <th>Type</th>
                  <th>Franchisee</th>
                  <th>Parent Unit</th>
                  <th>Stores</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgUnits.map((unit) => (
                  <tr key={unit.id}>
                    <td>
                      <div>
                        <div style={{ fontWeight: '600', color: '#212529' }}>{unit.name}</div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>ID: {unit.id}</div>
                      </div>
                    </td>
                    <td>
                      <span style={{ 
                        textTransform: 'capitalize',
                        color: '#495057',
                        fontWeight: '500'
                      }}>
                        {unit.type}
                      </span>
                    </td>
                    <td style={{ color: '#495057' }}>{unit.franchiseeName}</td>
                    <td style={{ color: '#495057' }}>
                      {unit.parentId ? orgUnits.find(o => o.id === unit.parentId)?.name || 'None' : 'None'}
                    </td>
                    <td style={{ color: '#495057' }}>{unit.storeCount}</td>
                    <td>
                      <span className={`status-badge ${unit.status}`}>
                        {unit.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn-modern-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>
                          Edit
                        </button>
                        <button className="btn-modern-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>
                          Hierarchy
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stores Tab */}
      {activeTab === 'stores' && (
        <div style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Store Code</th>
                  <th>Address</th>
                  <th>Concept</th>
                  <th>Franchisee</th>
                  <th>Org Unit</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stores.map((store) => (
                  <tr key={store.id}>
                    <td>
                      <div>
                        <div style={{ fontWeight: '600', color: '#212529' }}>{store.name}</div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>ID: {store.storeId || store.id}</div>
                      </div>
                    </td>
                    <td>
                      <div>
                        {store.storeCode ? (
                          <div>
                            <div style={{ fontWeight: '600', color: '#28a745', fontSize: '14px' }}>{store.storeCode}</div>
                            <div style={{ fontSize: '11px', color: '#6c757d' }}>📱 POS Code</div>
                          </div>
                        ) : (
                          <div style={{ fontSize: '12px', color: '#dc3545', fontStyle: 'italic' }}>
                            No code assigned
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ color: '#495057' }}>{formatAddress(store.address)}</td>
                    <td style={{ color: '#495057' }}>{store.conceptName}</td>
                    <td style={{ color: '#495057' }}>{store.franchiseeName || 'Corporate'}</td>
                    <td style={{ color: '#495057' }}>{store.orgUnitName || 'Direct'}</td>
                    <td>
                      <span style={{ 
                        textTransform: 'capitalize',
                        color: '#495057',
                        fontSize: '12px'
                      }}>
                        {store.storeType?.replace('_', ' ') || store.type?.replace('_', ' ') || 'Unknown'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${store.status}`}>
                        {store.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
                        <button 
                          className="btn-modern-secondary" 
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                          onClick={() => handleEdit(store, 'store')}
                        >
                          Edit
                        </button>
                        <div style={{ position: 'relative' }}>
                          <button 
                            className="btn-modern-secondary" 
                            style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                            onClick={() => toggleOperationsDropdown(store.storeId || store.id)}
                          >
                            Operations
                            <span style={{ fontSize: '10px' }}>▼</span>
                          </button>
                          {showOperationsDropdown[store.storeId || store.id] && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              right: '0',
                              backgroundColor: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              zIndex: 1000,
                              minWidth: '180px',
                              maxWidth: '250px',
                              maxHeight: '300px',
                              overflowY: 'auto',
                              marginTop: '4px'
                            }}>
                              <button 
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  borderRadius: '0 0 6px 6px'
                                }}
                                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                                onClick={() => {
                                  handleTableChartManagement(store);
                                }}
                              >
                                Table Chart Management
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Configuration Actions */}
      <div className="dashboard-grid" style={{ marginTop: '32px' }}>
        <div className="dashboard-card">
          <h3>Hierarchy Configuration</h3>
          <p>Configure the organizational structure and permissions.</p>
          <button className="btn-modern-accent">Configure Hierarchy</button>
        </div>
        
        <div className="dashboard-card">
          <h3>Permission Templates</h3>
          <p>Set up role-based permissions for each level.</p>
          <button className="btn-modern-accent">Manage Permissions</button>
        </div>
        
        <div className="dashboard-card">
          <h3>Bulk Operations</h3>
          <p>Perform bulk updates across multiple locations.</p>
          <button className="btn-modern-accent">Bulk Update</button>
        </div>
        
        <div className="dashboard-card">
          <h3>Reporting & Analytics</h3>
          <p>View hierarchy performance and analytics.</p>
          <button className="btn-modern-accent">View Reports</button>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New {addType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
            </div>
            <div className="modal-body">
              {addType === 'enterprise' && (
                <EnterpriseForm onSubmit={handleCreateEnterprise} onCancel={() => setShowAddModal(false)} />
              )}

              {addType === 'concept' && (
                <ConceptForm 
                  enterprises={enterprises}
                  onSubmit={handleCreateConcept} 
                  onCancel={() => setShowAddModal(false)} 
                />
              )}

              {addType === 'franchisee' && (
                <FranchiseeForm 
                  enterprises={enterprises}
                  onSubmit={handleCreateFranchisee} 
                  onCancel={() => setShowAddModal(false)} 
                />
              )}

              {addType === 'store' && (
                <StoreForm 
                  enterprises={enterprises}
                  onSubmit={handleCreateStore} 
                  onCancel={() => setShowAddModal(false)} 
                />
              )}

              {addType === 'org_unit' && (
                <OrganizationalUnitForm 
                  enterprises={enterprises}
                  onSubmit={handleCreateOrgUnit} 
                  onCancel={() => setShowAddModal(false)} 
                />
              )}

            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit {editingItem.type?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</h3>
            </div>
            <div className="modal-body">
              {editingItem.type === 'store' && (
                <StoreEditForm
                  store={editingItem}
                  enterprises={enterprises}
                  onSubmit={handleSaveEdit}
                  onCancel={() => {
                    setShowEditModal(false);
                    setEditingItem(null);
                  }}
                />
              )}

              {editingItem.type === 'enterprise' && (
                <EnterpriseForm
                  initialData={editingItem}
                  onSubmit={handleSaveEdit}
                  onCancel={() => {
                    setShowEditModal(false);
                    setEditingItem(null);
                  }}
                />
              )}

              {editingItem.type === 'concept' && (
                <ConceptForm
                  enterprises={enterprises}
                  initialData={editingItem}
                  onSubmit={handleSaveEdit}
                  onCancel={() => {
                    setShowEditModal(false);
                    setEditingItem(null);
                  }}
                />
              )}

              {editingItem.type === 'franchisee' && (
                <FranchiseeForm
                  enterprises={enterprises}
                  initialData={editingItem}
                  onSubmit={handleSaveEdit}
                  onCancel={() => {
                    setShowEditModal(false);
                    setEditingItem(null);
                  }}
                />
              )}

              {!['store', 'enterprise', 'concept', 'franchisee'].includes(editingItem.type) && (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <p>Edit form for {editingItem.type} is not yet implemented.</p>
                  <button
                    className="btn-modern btn-modern-secondary"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingItem(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table Chart Management */}
      {selectedStoreForTableChart && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{ 
            width: '1200px', 
            height: '800px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Table Chart Management</h3>
                <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '4px' }}>
                  Store: {stores.find(s => (s.storeId || s.id) === selectedStoreForTableChart)?.name}
                </div>
              </div>
              <button 
                onClick={() => setSelectedStoreForTableChart(null)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ 
              flex: 1, 
              overflow: 'hidden'
            }}>
              <TableChartManager
                restaurantId={selectedStoreForTableChart}
                onClose={() => setSelectedStoreForTableChart(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>;
};

export default RestaurantManagement;